import React, { useState, useEffect, useRef  } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Globe, Copy, Check, QrCode, Share2 } from 'lucide-react';
import SidebarNavigation from '../../components/navigation/SidebarNavigation';
import { QRCodeCanvas } from 'qrcode.react';

export default function PublicBookingUrlManagementScreen() {
  const { userProfile, loading } = useAuth();
  const [copied, setCopied] = useState(false);
  const [publicUrl, setPublicUrl] = useState('');

  useEffect(() => {
    // CRITICAL FIX: Use userProfile.id which is the psychologist's ID (NOT user_id)
    // userProfile is loaded from psychologists table, so userProfile.id = psychologists.id
    if (userProfile?.id) {
      const baseUrl = window.location?.origin;
      // Using psychologist's ID from the psychologists table
      const url = `${baseUrl}/public-patient-booking-screen/${userProfile?.id}`;
      setPublicUrl(url);
      
      // Debug logging to verify correct ID is being used
      console.log('🔗 Public booking URL generated:', {
        psychologistId: userProfile?.id,
        userId: userProfile?.user_id,
        url: url
      });
    }
  }, [userProfile]);

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard?.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy URL:', error);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Agende sua consulta',
          text: 'Acesse o link para agendar sua consulta',
          url: publicUrl,
        });
      } catch (error) {
        console.error('Failed to share:', error);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen">
        <SidebarNavigation />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen" style={{ backgroundColor: '#d3d3d3' }}>
      <SidebarNavigation />
      <main className="flex-1 lg:ml-64">
        <div className="p-3 sm:p-4 lg:p-4 pt-4 sm:pt-4 lg:pt-6 lg:mt-0 max-w-full overflow-x-hidden">
          {/* Header - Standardized Format */}
          <div className="mb-8">
            <div className="bg-gray-100 rounded-xl shadow-lg shadow-md p-6 max-w-6xl">

              {/* Desktop */}
              <div className="hidden md:flex items-center gap-3 mb-2 flex-nowrap">
                <Globe className="w-8 h-8 text-[#969696]" />
  
                  <h1 className="text-3xl font-bold text-[#969696] font-manrope whitespace-nowrap">
                  Agenda Pública
                </h1>
              </div>

              {/* Desktop - Mostra subtítulo */}
              <p className="hidden md:block text-[#969696] font-manrope text-lg">
                Divulgue para novos agendamentos
              </p>

              {/* Mobile */}
              <div className="md:hidden flex items-center gap-2 mb-1">
                <Globe className="w-8 h-8 text-[#969696]" />

                <h1 className="text-2xl font-bold text-[#969696] font-manrope">
                  Agenda Pública
                </h1>
              </div>

            </div>
          </div>

          {/* URL Display Card */}
          <div className="bg-white rounded-xl shadow-lg shadow-md border border-gray-200 p-6 mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Seu link público de agendamento
            </label>
            
            <div className="flex flex-col md:flex-row gap-3">
              <div className="flex-1 bg-gray-50 border border-gray-300 rounded-lg p-4 font-mono text-sm break-all">
                {publicUrl || 'Carregando...'}
              </div>
              
              <button
                onClick={handleCopyUrl}
                disabled={!publicUrl}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-w-[140px]"
              >
                {copied ? (
                  <>
                    <Check className="w-5 h-5" />
                    <span>Copiado!</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-5 h-5" />
                    <span>Copiar Link</span>
                  </>
                )}
              </button>
            </div>
          </div>
          
          {/* Information + QR Code Section */}
          <div className="bg-blue-50 rounded-xl shadow-lg p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-[3fr_2fr] gap-6 md:gap-8 items-start">
              
              {/* Coluna 1 - Como funciona? (permanece igual) */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-8">
                  Como funciona?
                </h3>
                <ul className="space-y-6 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold">•</span>
                    <span>Compartilhe este link em suas redes sociais, site ou cartão de visitas</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold">•</span>
                    <span>Pacientes podem agendar consultas diretamente sem precisar criar conta</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold">•</span>
                    <span>Você receberá notificações de todos os novos agendamentos</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold">•</span>
                    <span>Habilite ou desabilite facilmente no menu Notificações</span>
                  </li>              
                </ul>
              </div>

              {/* Coluna 2 - QR Code */}
              <div className="flex flex-col items-center justify-center">
                {publicUrl && (
                  <div className="rounded-lg bg-white p-5 shadow-md">
                    <div id="qr-canvas-container">
                    <QRCodeCanvas
                      value={publicUrl}
                      size={180}
                      level="Q"
                      bgColor="#ffffff"
                      fgColor="#111827"
                    />
                    </div>

                    {/* Botão Copiar QR Code */}
                    <button
                      onClick={async () => {
                        const container = document.getElementById('qr-canvas-container');
                        if (!container) {
                          alert('Container do QR Code não encontrado.');
                          return;
                        }

                        const canvas = container.querySelector('canvas');
                        if (!canvas) {
                          alert('Canvas do QR Code não encontrado. Certifique-se de que o QR foi gerado.');
                          return;
                        }

                        try {
                          const dataUrl = canvas.toDataURL('image/png');
                          const blob = await (await fetch(dataUrl)).blob();
                          await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
                          alert('QR Code copiado para a área de transferência! Cole onde quiser (Ctrl+V).');
                        } catch (err) {
                          console.error('Erro ao copiar QR Code:', err);
                          alert('Não foi possível copiar a imagem. Tente o botão de download como alternativa.');
                        }
                      }}
                      className="ml-2 mt-4 flex items-center gap-2 rounded bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-indigo-700 transition"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      Copiar QR Code
                    </button>

                  </div>
                  
                )}

                {/* Se não tiver gerado ainda, mostra placeholder ou mensagem */}
                {!publicUrl && (
                  <div className="flex h-52 w-52 items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-white text-gray-400">
                    QR Code aparecerá aqui
                  </div>
                )}
              </div>

            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-center gap-4">
            <button
              onClick={handleShare}
              disabled={!publicUrl || !navigator.share}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Share2 className="w-5 h-5 text-gray-600" />
              <span className="text-gray-700">Compartilhar</span>
            </button>
          </div>

          {/* Statistics Section (Placeholder) */}
          <div className="mt-8 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Estatísticas de Uso
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-3xl font-bold text-blue-600">--</p>
                <p className="text-sm text-gray-600 mt-1">Cliques no link</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-3xl font-bold text-green-600">--</p>
                <p className="text-sm text-gray-600 mt-1">Agendamentos</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg col-span-2 md:col-span-1">
                <p className="text-3xl font-bold text-purple-600">--%</p>
                <p className="text-sm text-gray-600 mt-1">Taxa de conversão</p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}